export const ZAD_SALES_HISTORY_TZ = 'Africa/Tunis'
export const ZAD_SALES_HISTORY_MAX_RANGE_DAYS = 366

export type ZadSalesHistoryPreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'custom'

export type ZadSalesHistoryDiscountFilter = 'ANY' | 'WITH_DISCOUNT' | 'WITHOUT_DISCOUNT'
export type ZadSalesHistoryCustomerKind = '' | 'WALK_IN' | 'REGISTERED'
export type ZadSalesHistoryDiscountSource = 'NONE' | 'ITEM' | 'GLOBAL' | 'PROMO' | 'MULTIPLE'

export type ZadSalesHistoryProductSortField =
  | 'netSales'
  | 'quantitySold'
  | 'grossSales'
  | 'totalDiscount'
  | 'productName'
  | 'returnValue'
  | 'netCommercialValue'

export type ZadSalesHistorySaleSortField = 'saleDate' | 'saleNumber' | 'netTotal' | 'gross' | 'quantity'

export type ZadSalesHistoryPeriodMode = 'date' | 'range'

export interface ZadSalesHistoryPeriod {
  preset: ZadSalesHistoryPreset
  mode: ZadSalesHistoryPeriodMode
  date?: string
  from: string
  to: string
}

export interface ZadSalesHistoryQuery {
  period: ZadSalesHistoryPeriod
  customerKind?: ZadSalesHistoryCustomerKind
  discount?: ZadSalesHistoryDiscountFilter
  productPageNumber: number
  productPageCount: number
  productSortField: ZadSalesHistoryProductSortField
  productSortOrder: 'asc' | 'desc'
  salesPageNumber: number
  salesPageCount: number
  salesSortField: ZadSalesHistorySaleSortField
  salesSortOrder: 'asc' | 'desc'
  requestKey: string
}

export interface ZadSalesHistoryPeriodMeta {
  from: string
  to: string
  timezone?: string
  dateFieldSales?: string
  dateFieldReturns?: string
  dateFieldPayments?: string
}

export interface ZadSalesHistoryDailyBucket {
  date: string
  salesCount?: number
  quantitySold: number
  grossSales: number
  totalDiscount?: number
  netSales: number
  returnsCount?: number
  quantityReturned: number
  returnValue: number
  netSalesAfterReturns: number
  collectedAmount?: number
  refundedAmount?: number
  netCashCollected?: number
  costTotal?: number
  grossProfit?: number
  marginPercent?: number | null
}

export interface ZadSalesHistoryMoneyIn {
  total: number
  byMethod?: Partial<Record<'CASH' | 'CARD' | 'CHEQUE' | 'BANK_TRANSFER' | 'OTHER', number>>
  dateField?: string
}

export interface ZadSalesHistoryRefundsOut {
  total: number
  dateField?: string
}

export interface ZadSalesHistoryTopProduct {
  productId: string
  productNameSnapshot?: string
  quantitySold?: number
  netSales?: number
  netCommercialValue?: number
}

export interface ZadSalesHistorySummary {
  period?: ZadSalesHistoryPeriodMeta
  salesCount: number
  distinctProductsSold: number
  quantitySold: number
  grossSales: number
  itemDiscountTotal?: number
  globalDiscountTotal?: number
  promotionDiscountTotal?: number
  promoCodeDiscountTotal?: number
  totalDiscount: number
  netSales: number
  returnsCount?: number
  quantityReturned: number
  returnValue: number
  netSalesAfterReturns: number
  averageTicket?: number | null
  averageUnitPrice?: number | null
  costTotal?: number
  grossProfit?: number
  marginPercent?: number | null
  daily?: ZadSalesHistoryDailyBucket[]
  topProducts?: ZadSalesHistoryTopProduct[]
  moneyIn?: ZadSalesHistoryMoneyIn
  refundsOut?: ZadSalesHistoryRefundsOut
  collectedAmount?: number
  refundedAmount?: number
  netCashCollected?: number
  outstandingReceivables?: number
  outstandingAsOf?: string
}

export interface ZadSalesHistoryProductRow {
  productId: string
  productCodeSnapshot?: string
  productNameSnapshot?: string
  barcodeSnapshot?: string | null
  quantitySold: number
  salesCount?: number
  grossSales: number
  itemDiscount?: number
  allocatedGlobalDiscount?: number
  promotionDiscount?: number
  allocatedPromoDiscount?: number
  totalDiscount: number
  netSales: number
  quantityReturned: number
  returnValue: number
  netQuantity?: number
  netCommercialValue: number
  hasDiscount: boolean
  discountedQuantity?: number
  nonDiscountedQuantity?: number
  basePriceMin?: number
  basePriceMax?: number
  averageEffectiveUnitPrice?: number
}

export interface ZadSalesHistoryProductsPage {
  period?: ZadSalesHistoryPeriodMeta
  products: ZadSalesHistoryProductRow[]
  total: number
  totalPages: number
  pageNumber: number
  pageCount: number
}

export interface ZadSalesHistorySaleOccurrence {
  saleId: string
  saleNumber?: string
  saleDate?: string
  validatedAt?: string
  businessDate?: string
  productId?: string
  productNameSnapshot?: string
  productCodeSnapshot?: string
  barcodeSnapshot?: string | null
  quantity: number
  saleUnit?: 'UNIT' | 'PACKAGE' | string
  packagingLabelSnapshot?: string | null
  unitsPerPackageSnapshot?: number | null
  baseUnitPrice?: number
  grossLineValue?: number
  itemDiscount?: number
  allocatedGlobalDiscount?: number
  promotionDiscount?: number
  promoDiscount?: number
  totalDiscount?: number
  finalLineRevenue?: number
  effectiveUnitPrice?: number
  hasDiscount?: boolean
  discountSource?: ZadSalesHistoryDiscountSource | string
  customerKind?: 'WALK_IN' | 'REGISTERED' | string
  customerNameSnapshot?: string | null
  createdBy?: string | null
  createdByName?: string | null
  validatedBy?: string | null
  validatedByName?: string | null
  promoCode?: string | null
  unitCostSnapshot?: number
  lineCost?: number
  lineMargin?: number
}

export interface ZadSalesHistoryReturnOccurrence {
  returnId: string
  returnNumber?: string
  returnDate?: string
  businessDate?: string
  saleId?: string
  saleNumber?: string
  productId?: string
  productNameSnapshot?: string
  productCodeSnapshot?: string
  quantity: number
  lineReturnValue?: number
  unitReturnValue?: number
}

export interface ZadSalesHistoryProductDaily {
  date: string
  quantitySold: number
  grossSales?: number
  totalDiscount?: number
  netSales: number
  quantityReturned: number
  returnValue: number
  netCommercialValue: number
}

export interface ZadSalesHistoryProductDetail {
  period?: ZadSalesHistoryPeriodMeta
  productId: string
  productCodeSnapshot?: string
  productNameSnapshot?: string
  barcodeSnapshot?: string | null
  quantitySold: number
  quantityReturned: number
  grossSales: number
  totalDiscount: number
  netSales: number
  returnValue: number
  netQuantity?: number
  netCommercialValue: number
  hasDiscount?: boolean
  discountedQuantity?: number
  nonDiscountedQuantity?: number
  basePriceMin?: number
  basePriceMax?: number
  averageEffectiveUnitPrice?: number
  occurrences?: ZadSalesHistorySaleOccurrence[]
  returns?: ZadSalesHistoryReturnOccurrence[]
  daily?: ZadSalesHistoryProductDaily[]
}

export interface ZadSalesHistorySaleRow {
  saleId: string
  saleNumber?: string
  businessDate?: string
  saleDate?: string
  validatedAt?: string
  createdBy?: string | null
  createdByName?: string | null
  validatedBy?: string | null
  validatedByName?: string | null
  customerKind?: 'WALK_IN' | 'REGISTERED' | string
  customerName?: string | null
  itemsCount?: number
  quantity?: number
  gross?: number
  discount?: number
  netTotal?: number
  returnValue?: number
  netAfterReturns?: number
  collectedAmount?: number
  remainingAmount?: number
  paymentStatus?: 'UNPAID' | 'PARTIAL' | 'SETTLED' | string
  paymentDates?: string[]
  settlement?: {
    status?: 'UNPAID' | 'PARTIAL' | 'SETTLED' | string
    saleAmount?: number
    paidAmount?: number
    remainingAmount?: number
  } | null
  costTotal?: number
  grossProfit?: number
}

export interface ZadSalesHistorySalesPage {
  period?: ZadSalesHistoryPeriodMeta
  sales: ZadSalesHistorySaleRow[]
  total: number
  totalPages: number
  pageNumber: number
  pageCount: number
}
